<template>
    <div class="ScenarioItem" :class="{ collapsed }">
        <template v-if="!hideName">
            <div
                class="title header --global-clickable"
                @xclick.stop="isJourneyUnlocked || journeyIsLockedButHasProgress ? (isCollapsed = !isCollapsed) : null"
                @click.stop="isJourneyUnlocked || journeyIsLockedButHasProgress ? (isCollapsed = !isCollapsed) : null"
            >
                <JourneyIcon class="journeyIcon" type="practice" :label="`${journey.index + 1}`" />

                <div class="headerText">
                    <div class="headerTitle">
                        {{ journey.name || alternativeScenarioTitle }}
                    </div>
                    <p v-if="isJourneyCompleted && !isJourneyUnlocked" class="optionally-paragraph">
                        {{ $t('completedInRelatedLifeskill') }}
                    </p>
                    <p v-else-if="journeyIsLockedButHasProgress" class="optionally-paragraph">
                        {{ $t('lockedWithProgress') }}
                    </p>
                    <p v-if="!completed">{{ $t('completeJourney') }}</p>

                    <div v-if="!(isJourneyCompleted && !isJourneyUnlocked)" class="text">
                        <span>
                            {{ $t('journeyProgress', { percent: percentageProgress }) }}
                        </span>
                    </div>
                    <div class="progress-bar-wrapper">
                        <div
                            v-if="percentageProgress !== 0 && percentageProgress !== 100"
                            class="progress-bar"
                            :style="{ width: percentageProgress + '%' }"
                        />
                    </div>
                </div>

                <div v-if="isJourneyUnlocked || journeyIsLockedButHasProgress" class="buttonToggleCollapse">
                    <svgicon class="buttonToggleCollapseIcon" icon="chevron-down" :xstyle="{ fill: theme.color }" />
                </div>

                <a v-else class="button-blue">
                    {{ $t('locked') }}
                </a>
            </div>
        </template>

        <TransitionExpandCollapse>
            <div v-if="collapsed" class="paddingBox">
                <div class="paddingBoxTopSpacer" />

                <PracticeVerticalTimeline
                    :items="items"
                    extend-top
                    :content-overflow-hidden="false"
                    :line-style="{ dasharray: [2, 2], color: 'rgba(0, 0, 0, 0.1)' }"
                    :active-line-style="{ color: themeColor }"
                    :course-id="courseId"
                    :is-journey-unlocked="isJourneyUnlocked"
                    :journey-is-locked-but-has-progress="journeyIsLockedButHasProgress"
                    :did-user-scenario-today="didUserScenarioToday"
                >
                    <template slot="bullet" slot-scope="props">
                        <StepBullet
                            :current="props.item.current"
                            :progress="props.item.progress"
                            :icon="{ masterquiz: 'flag-with-checkmark' }[props.type] || 'steps-3'"
                        />
                    </template>
                </PracticeVerticalTimeline>
                <div class="paddingBoxBottomSpacer" />
            </div>
        </TransitionExpandCollapse>
    </div>
</template>

<translations>
  journeyProgress: '{percent}% completed'
  journeyProgress_no: 'Du har fullført {percent}% av reisen'
  journeyCompleted: Completed
  journeyCompleted_no: Fullført
  completedInRelatedLifeskill: "You've completed 100% of the journey (in a related skill)"
  completedInRelatedLifeskill_no: Du har fullført 100% av ferdigheten (i en relatert ferdighet)
  completeJourney: Complete theory journey first
  completeJourney_no: Fullfør teorireisen først
  lockedWithProgress: Started in related lifeskill
  lockedWithProgress_no: Startet i relatert ferdighet
  scenarioJourney: Scenario Journey
  scenarioJourney_no: Scenario Reise
  show: Show
  show_no: Vis
  locked: Locked
  locked_no: Låst
</translations>

<script>
import { mapGetters } from 'vuex';
import { getStepGroupTitle } from '@/utils';
import PracticeVerticalTimeline from '@/components/PracticeVerticalTimeline';
import TransitionExpandCollapse from '@/components/TransitionExpandCollapse';
import UpgradeButton from '@/modules/dashboard/components/UpgradeButton';
import { cssVariables } from '@/category-themes';

import JourneyQuizItem from './JourneyQuizItem';
import StepBullet from './StepBullet';
import JourneyIcon from './JourneyIcon';

export default {
    components: {
        TransitionExpandCollapse,
        PracticeVerticalTimeline,
        StepBullet,
        JourneyIcon
    },
    inject: ['theme'],
    props: {
        useEllipsis: {
            type: Boolean,
            default: false
        },
        hideActivities: {
            type: Boolean,
            default: false
        },
        hideName: {
            type: Boolean,
            default: false
        },
        journey: {
            type: Object,
            required: true
        },
        courseId: {
            type: String,
            required: true
        },
        didUserScenarioToday: {
            type: Boolean,
            required: true
        },
        isCurrentJourney: {
            type: Boolean,
            required: true
        }
    },

    data() {
        return {
            isCollapsed: true
        };
    },

    computed: {
        ...mapGetters({
            getStepById: 'moduleCourse/getStepById',
            getScenarioById: 'moduleCourse/getScenarioById',
            getJourneyById: 'moduleCourse/getJourneyById'
        }),

        isJourneyUnlocked() {
            return this.getJourneyById(this.journey.id).quiz.done !== 0;
        },

        collapsed() {
            return this.isCollapsed && this.isCurrentJourney;
        },

        journeyIsLockedButHasProgress() {
            return this.getJourneyById(this.journey.id).quiz.done === 0 && this.percentageProgress > 0;
        },

        isJourneyCompleted() {
            return this.percentageProgress === 100;
        },

        alternativeScenarioTitle() {
            return `${this.$t('scenarioJourney')} ${this.journey.index + 1}`;
        },

        percentageProgress() {
            let doneCount = 0;
            let totalScenarios = 0;

            this.steps.forEach(step =>
                step.scenarios.forEach(scenario => {
                    if (scenario.done) {
                        doneCount++;
                    }
                    totalScenarios++;
                })
            );

            return Math.floor((doneCount / totalScenarios) * 100);
        },

        journeySteps() {
            return this.journey.stepIds.map(stepId => this.getStepById(stepId)).filter(step => step.scenarioIds);
        },

        items() {
            let foundCurrent = false;

            return this.steps.map((step, i) => {
                return {
                    progress: step.scenarios.filter(scenario => scenario.done).length / step.scenarios.length,
                    current: step.scenarios.some(scenario => {
                        if (!scenario.done && !foundCurrent) {
                            foundCurrent = true;

                            return true;
                        }
                    }),
                    title: step.title,
                    scenarios: step.scenarios,
                    id: step.id
                };
            });
        },

        steps() {
            return this.journeySteps.map(step => {
                return {
                    scenarios: step.scenarioIds.map(scenarioId => this.getScenarioById(scenarioId)),
                    title: step.title,
                    id: step.id
                };
            });
        },

        themeColor() {
            return cssVariables.completionColor;
        },

        completed() {
            return this.percentageProgress === 100;
        }
    }
};
</script>

<style lang="scss" scoped>
$bulletColumnWidth: 4em;
$bulletColumnRightMargin: 2em;

.ScenarioItem {
    background-color: white;
    box-shadow: 0px 2px 23px 3px rgba(79, 79, 79, 0.1);
    padding: 1.2em 0;
    border-radius: 4px;
    .title {
        padding: 0 18.5px;
        width: 100%;
        @media (max-width: 530px) {
            padding: 0 8px;
        }
    }
    .PracticeVerticalTimeline {
        /deep/ .extendedTopLine {
            height: 1.5em;
        }
    }

    &.collapsed {
        .buttonToggleCollapseIcon {
            transform: rotate(0deg);
        }
    }

    &:not(.collapsed) {
        .buttonToggleCollapse {
            border: 1px solid $purple;
        }
    }

    /deep/ .Step {
        .bullet {
            width: 95px;
            margin-right: $bulletColumnRightMargin;
            @media (max-width: 530px) {
                width: 4em;
            }
        }
    }
}
.header {
    display: flex;
    align-items: center;
}

.headerText {
    flex: 1;
    padding-right: 10px;
    p {
        font-size: 16px;
        line-height: 22px;
        color: #727272;
        margin-top: 5px;
        margin-bottom: 10px;
        @media (max-width: 530px) {
            font-size: 14px;
        }
    }
    .optionally-paragraph {
        margin-top: 10px;
        margin-bottom: 10px;
        color: #727272;
    }
}

.headerTitle {
    font-size: 140%;
    font-weight: 600;
    margin-top: 15px;
    @media (max-width: 530px) {
        font-size: 16px;
    }
}

.journeyIcon {
    width: 96px;
    height: 96px;
    margin-right: $bulletColumnRightMargin;
    @media (max-width: 530px) {
        width: 66px;
        height: 66px;
    }
}

.buttonToggleCollapse {
    width: 35px;
    height: 35px;
    background-color: rgba(black, 0.03);
    border: 1px solid rgba(black, 0.02);
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    &:hover {
        border: 1px solid $purple;
    }
    @media (max-width: 1000px) {
        width: 25px;
        height: 25px;
    }
}

.buttonToggleCollapseIcon {
    width: 12px;
    height: 12px;
    fill: $completionColor;
    transition: transform 0.5s;
    transform: rotate(-180deg);
    @media (max-width: 1000px) {
        width: 7px;
        height: 7px;
    }
}

.progress-bar-wrapper {
    margin: 7.5px 0;
    background: #eaeaea;
    .progress-bar {
        width: 0;
        height: 3px;
        background-color: #5983f7;
        border-top-left-radius: 50% 50px;
        border-bottom-left-radius: 50% 50px;
        border-top-right-radius: 50% 50px;
        border-bottom-right-radius: 50% 50px;
    }
}

.label {
    font-weight: 600;
}
.text {
    font-size: 16px;
    line-height: 22px;
    color: #727272;
    margin-top: 10px;
    @media (max-width: 530px) {
        font-size: 14px;
    }
}

.paddingBox {
    // NOTE: needed to avoid cutting shadow while collapsing
    padding: 0 18.5px;
    background: white;
    @media (max-width: 530px) {
        padding: 0 8px;
    }
}
.paddingBoxTopSpacer,
.paddingBoxBottomSpacer {
    height: 1em;
}
</style>
